<script lang="ts">
  import type { Quotation } from '$lib/types';
  import { QUOTE_VERTICAL_META } from '$lib/types';
  import { formatMoney } from '$lib/utils/money';
  import { avatarColor, initials, relativeAge } from '$lib/utils/display';
  import type { LegalAction } from './dragTransitions';

  const {
    quote,
    designerLabel,
    onOpen,
    actions = [],
    onDragStart,
    onDragEnd,
    onAction
  }: {
    quote: Quotation;
    designerLabel: string;
    onOpen: () => void;
    actions?: LegalAction[];
    onDragStart?: () => void;
    onDragEnd?: () => void;
    onAction?: (action: LegalAction) => void;
  } = $props();

  const priced = $derived(['priced', 'approved'].includes(quote.status));
  const valueMinor = $derived(priced ? quote.grand_total_minor : quote.subtotal_minor);
  const draggable = $derived(actions.length > 0 && !!onDragStart);

  // The card is a div (not a <button>) rather than an interactive element,
  // since it hosts a nested <select> for keyboard-reachable quick actions —
  // a <select> nested inside a <button> is invalid/inaccessible HTML. The
  // div gets role="button" + tabindex + keydown handling to keep "click (or
  // Enter/Space) opens the quote" working exactly as before.
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  }

  // Native HTML5 drag-and-drop: setData is required by some browsers
  // (notably Firefox) for the drag to even start, even though this is a
  // same-page drag driven entirely by component state (onDragStart), not
  // by the payload itself.
  function handleDragStart(e: DragEvent) {
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', String(quote.id));
      e.dataTransfer.effectAllowed = 'move';
    }
    onDragStart?.();
  }

  function handleActionSelect(e: Event) {
    const select = e.currentTarget as HTMLSelectElement;
    const idx = Number(select.value);
    select.value = '';
    if (!Number.isNaN(idx) && actions[idx]) onAction?.(actions[idx]);
  }
</script>

<div
  class="pq-quote-card w-full text-left"
  role="button"
  tabindex="0"
  draggable={draggable}
  aria-label="Open quotation {quote.quotation_number} for {quote.client_name || 'untitled client'}"
  onclick={onOpen}
  onkeydown={handleKeydown}
  ondragstart={handleDragStart}
  ondragend={onDragEnd}
>
  <div class="flex items-center justify-between mb-2.5">
    <span class="text-[10.5px] font-semibold tracking-[.04em] uppercase text-toyama-teal bg-toyama-teal/10 rounded-[5px] px-[7px] py-0.5">
      {QUOTE_VERTICAL_META[quote.vertical]?.label ?? quote.vertical}
    </span>
    <div class="flex items-center gap-2">
      <span class="text-[10.5px] font-semibold tracking-[.04em] uppercase text-dark-gray">{quote.lot_label}</span>
      <span class="text-[11px] text-dark-gray font-mono">{quote.quotation_number}</span>
    </div>
  </div>
  <div class="font-heading font-medium text-[15px] text-navy leading-tight mb-0.5">
    {quote.client_name || 'Untitled client'}
  </div>
  <div class="text-[12.5px] text-dark-gray mb-3 truncate">{quote.project_name || 'Untitled project'}</div>
  <div class="flex items-center justify-between">
    <div>
      <div class="text-[9.5px] text-dark-gray uppercase tracking-[.05em]">
        {priced ? 'Total' : 'Estimate'}
      </div>
      <div class="font-heading font-bold text-[15px] text-navy">{formatMoney(valueMinor, quote.currency)}</div>
    </div>
    <div class="flex items-center gap-2">
      <span class="text-[11px] text-dark-gray">{relativeAge(quote.updated_at)}</span>
      <span
        title={designerLabel}
        class="w-[26px] h-[26px] rounded-full text-white text-[11px] font-semibold flex items-center justify-center shrink-0"
        style="background:{avatarColor(designerLabel)}"
      >
        {initials(designerLabel)}
      </span>
    </div>
  </div>
  {#if actions.length > 0}
    <!-- Keyboard-reachable equivalent of the drag: a native <select> needs
         no custom listbox/ARIA to be focusable and operable, and covers
         every legal transition (including "Approve", which has no drop
         target now that Approved isn't a board column). -->
    <div class="mt-2.5 pt-2.5 border-t border-toyama-cloudy-gray/60">
      <select
        class="pq-card-action-select"
        aria-label="Move quotation {quote.quotation_number}"
        draggable="false"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => e.stopPropagation()}
        onchange={handleActionSelect}
      >
        <option value="" selected>Move&hellip;</option>
        {#each actions as action, i (action.id)}
          <option value={i}>{action.label}</option>
        {/each}
      </select>
    </div>
  {/if}
</div>
